/*
 * BACHGOLD ANIMATIONS
 * -------------------
 * Reagiert auf [data-bg-animate="..."] Elemente:
 *  - "fade-up": opacity 0 + translateY(24px) → opacity 1 + translateY(0)
 *  - "fade-in": opacity 0 → 1
 *  - "fade-left": translateX(-24px) → 0
 *  - "fade-right": translateX(24px) → 0
 *
 * Trigger: IntersectionObserver, threshold 0.15.
 * Already-visible elements (above the fold) → instant `.bg-in` class (no animation).
 * Reduced-motion: alle Elemente bekommen `.bg-in` direkt, keine transition.
 *
 * Counter-Animation (für .bg-stats__number):
 *  Wenn Element numerische Zahl enthält (mit optionalem ".", "," thousands-sep
 *  und Suffix wie "k", "%"), wird über 3 Sekunden hochgezählt.
 *  Non-numeric strings ("Log 8", "Seit 2024") → fade-in only.
 */
(function () {
  'use strict';

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ────────────────────────────────────────
  // FADE-IN ANIMATIONS
  // ────────────────────────────────────────
  function initFadeAnimations() {
    var els = document.querySelectorAll('[data-bg-animate]');
    if (!els.length) return;

    if (prefersReducedMotion) {
      els.forEach(function (el) { el.classList.add('bg-in'); });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('bg-in');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' });

    els.forEach(function (el) {
      // Already-visible (above the fold) → instant
      var rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.85 && rect.bottom > 0) {
        el.classList.add('bg-in');
      } else {
        observer.observe(el);
      }
    });
  }

  // ────────────────────────────────────────
  // STATS COUNTER ANIMATION (3s ramp)
  // ────────────────────────────────────────
  function parseNumber(str) {
    // Returns { numeric: int, prefix: '', suffix: '', thousandsSep: '.' or ',' or '' }
    // or null if non-numeric
    var match = String(str).trim().match(/^([^\d-]*)([-+]?[\d.,\s]+)(.*)$/);
    if (!match) return null;
    var prefix = match[1];
    var numStr = match[2].trim();
    var suffix = match[3];

    // Detect thousands separator
    var thousandsSep = '';
    var cleanNum = numStr;
    // Format like "100.000" or "1.500" → period thousands
    if (/^\d{1,3}(\.\d{3})+$/.test(numStr)) {
      thousandsSep = '.';
      cleanNum = numStr.replace(/\./g, '');
    } else if (/^\d{1,3}(,\d{3})+$/.test(numStr)) {
      thousandsSep = ',';
      cleanNum = numStr.replace(/,/g, '');
    } else {
      cleanNum = numStr.replace(/[\s.,]/g, '');
    }

    var numeric = parseInt(cleanNum, 10);
    if (isNaN(numeric)) return null;
    return { numeric: numeric, prefix: prefix, suffix: suffix, thousandsSep: thousandsSep };
  }

  function formatWithThousands(n, sep) {
    if (!sep) return String(n);
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, sep);
  }

  function animateCounter(el, parsed, duration) {
    // Start at 50% of target → animate to 100% (faster perceived ramp)
    var startValue = Math.floor(parsed.numeric * 0.5);
    var deltaValue = parsed.numeric - startValue;
    var startTime = performance.now();
    function tick(now) {
      var t = Math.min(1, (now - startTime) / duration);
      var eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
      var current = startValue + Math.floor(deltaValue * eased);
      el.textContent = parsed.prefix + formatWithThousands(current, parsed.thousandsSep) + parsed.suffix;
      if (t < 1) requestAnimationFrame(tick);
      else el.textContent = parsed.prefix + formatWithThousands(parsed.numeric, parsed.thousandsSep) + parsed.suffix;
    }
    requestAnimationFrame(tick);
  }

  function initStatsCounter() {
    var nums = document.querySelectorAll('.bg-stats__number');
    if (!nums.length) return;

    nums.forEach(function (el) {
      var original = el.textContent.trim();
      var parsed = parseNumber(original);
      if (!parsed) return; // non-numeric: skip counter, fade-in regelt das
      el.setAttribute('data-bg-stat-target', original);
      // Initial: 50% des Zielwerts (statt 0) — wirkt schneller
      var startValue = Math.floor(parsed.numeric * 0.5);
      el.textContent = parsed.prefix + formatWithThousands(startValue, parsed.thousandsSep) + parsed.suffix;
    });

    if (prefersReducedMotion) {
      // Reduced motion: zeige direkt das Zielwert
      nums.forEach(function (el) {
        var target = el.getAttribute('data-bg-stat-target');
        if (target) el.textContent = target;
      });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          var el = entry.target;
          var target = el.getAttribute('data-bg-stat-target');
          if (!target) return;
          var parsed = parseNumber(target);
          if (parsed) animateCounter(el, parsed, 2000);
          observer.unobserve(el);
        }
      });
    }, { threshold: 0.4, rootMargin: '0px 0px -60px 0px' });

    nums.forEach(function (el) {
      if (el.hasAttribute('data-bg-stat-target')) observer.observe(el);
    });
  }

  // ────────────────────────────────────────
  // ANCHOR-LINKS → MODAL (Hero-Buttons öffnen direkt das CTA-Modal)
  // ────────────────────────────────────────
  function initAnchorToModal() {
    document.addEventListener('click', function (e) {
      var link = e.target.closest('a[href="#kontakt"], a[href="#bewerbung"]');
      if (!link) return;
      // Find closest modal-trigger (data-bg-supp-open inside data-bg-supp root)
      var modalRoots = document.querySelectorAll('[data-bg-supp]');
      for (var i = 0; i < modalRoots.length; i++) {
        var trigger = modalRoots[i].querySelector('[data-bg-supp-open]');
        if (trigger) {
          e.preventDefault();
          trigger.click();
          return;
        }
      }
      // Kein Modal vorhanden → normaler Anchor-Scroll (browser default)
    });
  }

  // ────────────────────────────────────────
  // INIT
  // ────────────────────────────────────────
  function init() {
    initFadeAnimations();
    initStatsCounter();
    initAnchorToModal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
